if (!customElements.get('conservation-project-map')) {
  customElements.define(
    'conservation-project-map',
    class ConservationProjectMap extends HTMLElement {
      connectedCallback() {
        this.panels = [...this.querySelectorAll('[data-pin]')];
        this.animals = [...this.querySelectorAll('.conservation-map__animal')];
        this.markers = new Map();
        this.animals.forEach((button) => button.addEventListener('click', () => this.selectAnimal(button.dataset.animal)));

        this.track = this.querySelector('.conservation-map__track');
        this.querySelectorAll('[data-step]').forEach((button) =>
          button.addEventListener('click', () => {
            const [first, second] = this.track.children;
            const step = second ? second.offsetLeft - first.offsetLeft : this.track.clientWidth;
            this.track.scrollBy({ left: step * Number(button.dataset.step) });
          })
        );

        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[data-pin]');
          if (panel) this.select(panel, true);
        });

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.observer.disconnect();
            this.initMap();
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.map?.remove();
      }

      initMap() {
        this.map = L.map(this.querySelector('[data-map]'), {
          scrollWheelZoom: false,
          dragging: !L.Browser.mobile,
        });
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          referrerPolicy: 'strict-origin-when-cross-origin',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        const size = Number(this.dataset.markerSize);
        const pins = this.panels
          .map((panel) => ({ panel, latlng: [parseFloat(panel.dataset.lat), parseFloat(panel.dataset.lng)] }))
          .filter(({ latlng }) => !latlng.some(Number.isNaN));

        if (pins.length) {
          this.map.fitBounds(
            pins.map(({ latlng }) => latlng),
            { padding: [size, size], maxZoom: Number(this.dataset.clickZoom) }
          );
        } else {
          this.map.setView([0, 0], 1);
        }

        const fullZoom = Number(this.dataset.clickZoom);
        const scaleMarkers = () => {
          const scale = Math.max(0.3, Math.min(1, 0.75 ** (fullZoom - this.map.getZoom())));
          this.map.getContainer().style.setProperty('--marker-scale', scale);
        };
        this.map.on('zoom', scaleMarkers);
        scaleMarkers();

        pins.forEach(({ panel, latlng }) => {
          const html = document.createElement('span');
          if (panel.dataset.marker) {
            const img = document.createElement('img');
            img.src = panel.dataset.marker;
            img.alt = '';
            html.append(img);
          }
          const marker = L.marker(latlng, {
            icon: L.divIcon({ className: 'conservation-map__marker', html, iconSize: [size, size] }),
            title: panel.dataset.title,
          }).addTo(this.map);
          marker.on('click', () => this.select(panel, true));
          marker.getElement().addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.select(panel, true);
          });
          this.markers.set(panel, marker);
        });

        this.select(this.panels.find((panel) => !panel.hidden) || this.panels[0]);
      }

      selectAnimal(animal) {
        const panels = this.panels.filter((panel) => panel.dataset.animal === animal);
        if (!panels.length) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.querySelector('[data-map]').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

        const latlngs = panels.map((panel) => this.markers.get(panel)?.getLatLng()).filter(Boolean);
        if (latlngs.length > 1) {
          this.select(panels[0]);
          const size = Number(this.dataset.markerSize);
          this.map.flyToBounds(latlngs, { padding: [size, size], maxZoom: Number(this.dataset.clickZoom), animate: !reduceMotion });
        } else {
          this.select(panels[0], true);
        }
      }

      select(panel, center = false) {
        if (!panel) return;
        this.panels.forEach((item) => (item.hidden = item !== panel));
        this.animals.forEach((button) => button.setAttribute('aria-pressed', button.dataset.animal === panel.dataset.animal && panel.dataset.animal !== ''));
        this.markers.forEach((marker, item) => {
          const active = item === panel;
          marker.getElement()?.classList.toggle('is-active', active);
          marker.setZIndexOffset(active ? 1000 : 0);
        });
        const marker = this.markers.get(panel);
        if (center && marker) {
          const zoom = Math.max(this.map.getZoom(), Number(this.dataset.clickZoom));
          this.map.flyTo(marker.getLatLng(), zoom, {
            animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
          });
        }
      }
    }
  );
}
